import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, startOfWeek } from "date-fns";
import { supabase } from "../../lib/supabase";
import { keys } from "../../lib/queryClient";
import type {
  Cadence,
  Database,
  Goal,
  GoalStats,
  Profile,
} from "../../lib/database.types";

export interface GoalWithStats extends Goal {
  stats: GoalStats | null;
}

async function fetchProfiles(): Promise<Profile[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .order("display_name");
  if (error) throw error;
  return data;
}

export function useProfiles() {
  return useQuery({
    queryKey: keys.profiles,
    queryFn: fetchProfiles,
    staleTime: 5 * 60_000,
  });
}

/**
 * Goals plus their stats. Two round trips rather than an embedded select,
 * because goal_stats is a view and PostgREST cannot infer a relationship to it.
 */
async function fetchGoalsWithStats(): Promise<GoalWithStats[]> {
  const [goals, stats] = await Promise.all([
    supabase.from("goals").select("*").order("created_at", { ascending: true }),
    supabase.from("goal_stats").select("*"),
  ]);

  if (goals.error) throw goals.error;
  if (stats.error) throw stats.error;

  const statsByGoal = new Map(stats.data.map((row) => [row.goal_id, row]));
  return goals.data.map((goal) => ({
    ...goal,
    stats: statsByGoal.get(goal.id) ?? null,
  }));
}

export function useGoals() {
  return useQuery({ queryKey: keys.goals, queryFn: fetchGoalsWithStats });
}

/**
 * Every period of the current week that has at least one check-in.
 *
 * The design states progress as "n of 7 this week" even for daily goals, which
 * goal_stats cannot answer — its period for a daily goal is a single day. This
 * view buckets check-ins into periods in the owner's own timezone, so counting
 * the complete ones gives the week.
 */
export type GoalPeriod = Database["public"]["Views"]["goal_periods"]["Row"];

async function fetchWeekPeriods(): Promise<GoalPeriod[]> {
  const weekStart = format(
    startOfWeek(new Date(), { weekStartsOn: 1 }),
    "yyyy-MM-dd",
  );
  const { data, error } = await supabase
    .from("goal_periods")
    .select("*")
    .gte("period_start", weekStart);
  if (error) throw error;
  return data;
}

export function useWeekPeriods() {
  return useQuery({ queryKey: keys.weekPeriods, queryFn: fetchWeekPeriods });
}

/**
 * How far into the week a goal is. Weekly goals answer this themselves; daily
 * ones are counted as days completed out of seven.
 */
export function weekProgress(
  goal: GoalWithStats,
  periods: GoalPeriod[] | undefined,
): { done: number; target: number } {
  if (goal.cadence === "weekly") {
    return {
      done: goal.stats?.period_count ?? 0,
      target: goal.target_per_period,
    };
  }
  const mine = periods?.filter((period) => period.goal_id === goal.id) ?? [];
  return {
    done: mine.filter((period) => period.is_complete).length,
    target: 7,
  };
}

export function useGoal(goalId: string | undefined) {
  const { data, ...rest } = useGoals();
  return { ...rest, data: data?.find((goal) => goal.id === goalId) };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface GoalDraft {
  title: string;
  description: string | null;
  cadence: Cadence;
  target_per_period: number;
  photo_required: boolean;
}

export function useCreateGoal(ownerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draft: GoalDraft) => {
      const { data, error } = await supabase
        .from("goals")
        .insert({ ...draft, owner_id: ownerId })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.goals });
      void queryClient.invalidateQueries({ queryKey: keys.recap });
    },
  });
}

/** Create the same goal for every member. Returns how many were created. */
export function useCreateGroupGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draft: GoalDraft) => {
      const { data, error } = await supabase.rpc("create_goal_for_group", {
        p_title: draft.title,
        p_description: draft.description,
        p_cadence: draft.cadence,
        p_target_per_period: draft.target_per_period,
        p_photo_required: draft.photo_required,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.goals });
      void queryClient.invalidateQueries({ queryKey: keys.recap });
    },
  });
}

export function useUpdateGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<GoalDraft>;
    }) => {
      const { data, error } = await supabase
        .from("goals")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.goals });
      void queryClient.invalidateQueries({ queryKey: keys.recap });
    },
  });
}

export function useArchiveGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase
        .from("goals")
        .update({ is_archived: archived })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.goals });
      void queryClient.invalidateQueries({ queryKey: keys.recap });
    },
  });
}

export function useDeleteGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("goals").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.goals });
      void queryClient.invalidateQueries({ queryKey: keys.feed });
    },
  });
}
